import { cn } from "@/lib/utils";
import { ChevronDown, Eye, EyeOff, Minus, Plus } from "lucide-react";
import { forwardRef, useId, useState } from "react";
import { pad2 } from "@/lib/format";

type FieldShellProps = {
  label: string;
  error?: string;
  className?: string;
};

const shell =
  "rounded-lg border bg-fx-input px-4 transition-[border-color,box-shadow] duration-[180ms] ease-fx focus-within:border-fx-red-line focus-within:ring-[3px] focus-within:ring-fx-focus-ring";

// `border-0 p-0 … focus:ring-0` neutralizam os estilos base do @tailwindcss/forms:
// a borda e o foco ficam no contêiner (`shell`), não no <input>.
const input =
  "w-full rounded-none border-0 bg-transparent p-0 text-[15px] text-fx-title shadow-none outline-none ring-0 placeholder:text-fx-faint focus:border-0 focus:shadow-none focus:ring-0 focus:outline-none disabled:opacity-50";

/** Campo com o rótulo dentro (66px), como no login e no cadastro. */
export const TextField = forwardRef<
  HTMLInputElement,
  FieldShellProps & React.ComponentProps<"input">
>(function TextField({ label, error, className, type, ...props }, ref) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const isPassword = type === "password";

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div
        className={cn(
          shell,
          "flex min-h-[62px] items-center gap-2",
          error ? "border-fx-danger" : "border-fx-line",
        )}
      >
        <label htmlFor={id} className="flex min-w-0 flex-1 flex-col justify-center gap-1 py-2.5">
          <span className={cn("fx-label", error && "text-fx-danger-ink")}>{label}</span>
          <input
            id={id}
            ref={ref}
            type={isPassword && visible ? "text" : type}
            aria-invalid={!!error}
            className={input}
            {...props}
          />
        </label>
        {isPassword && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
            className="flex size-11 shrink-0 items-center justify-center rounded-md text-fx-faint hover:text-fx-body"
          >
            {visible ? <EyeOff size={18} strokeWidth={1.6} /> : <Eye size={18} strokeWidth={1.6} />}
          </button>
        )}
      </div>
      {error && <p className="px-1 text-[12.5px] text-fx-danger-ink">{error}</p>}
    </div>
  );
});

export const TextAreaField = forwardRef<
  HTMLTextAreaElement,
  FieldShellProps & React.ComponentProps<"textarea">
>(function TextAreaField({ label, error, className, ...props }, ref) {
  const id = useId();
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className={cn("fx-label", error && "text-fx-danger-ink")}>
        {label}
      </label>
      <div className={cn(shell, "py-3", error ? "border-fx-danger" : "border-fx-line")}>
        <textarea
          id={id}
          ref={ref}
          rows={3}
          aria-invalid={!!error}
          className={cn(input, "min-h-[48px] resize-none leading-5")}
          {...props}
        />
      </div>
      {error && <p className="px-1 text-[12.5px] text-fx-danger-ink">{error}</p>}
    </div>
  );
});

/** Campo de data: rótulo dentro, valor `YYYY-MM-DD`. */
/** Select nativo com o rótulo dentro, no mesmo shell do `TextField`. */
export function SelectField({
  label,
  value,
  onChange,
  options,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn(shell, "relative flex min-h-[62px] items-center border-fx-line", className)}>
      <label htmlFor={id} className="flex min-w-0 flex-1 flex-col justify-center gap-1 py-2.5 pr-8">
        <span className="fx-label">{label}</span>
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(input, "cursor-pointer appearance-none bg-none [&>option]:bg-fx-surface")}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <ChevronDown
        size={18}
        strokeWidth={1.6}
        className="pointer-events-none absolute right-4 text-fx-faint"
      />
    </div>
  );
}

export const DateField = forwardRef<
  HTMLInputElement,
  FieldShellProps & React.ComponentProps<"input">
>(function DateField(props, ref) {
  return <TextField ref={ref} {...props} type="date" />;
});

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn("flex h-11 gap-1 rounded-full bg-fx-sunken p-1", className)}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            role="tab"
            type="button"
            aria-selected={on}
            onClick={() => onChange(o.value)}
            className={cn(
              "flex-1 rounded-full px-3 text-[13px] font-semibold transition-colors duration-[180ms] ease-fx",
              on ? "bg-white text-fx-bg" : "text-fx-muted hover:text-fx-body",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function QtyStepper({
  value,
  onChange,
  label,
  className,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-md border border-fx-line-subtle bg-fx-sunken",
        className,
      )}
    >
      <button
        type="button"
        aria-label={`Remover ${label}`}
        disabled={value === 0}
        onClick={() => onChange(Math.max(0, value - 1))}
        className="flex h-10 w-11 items-center justify-center text-fx-body transition-[opacity,transform] duration-[120ms] ease-fx active:scale-95 disabled:cursor-default disabled:opacity-35 md:w-11"
      >
        <Minus size={16} strokeWidth={2} />
      </button>
      <span
        aria-live="polite"
        className={cn("fx-num min-w-8 text-center text-[19px]", value > 0 ? "text-fx-cream" : "text-fx-faint")}
      >
        {value}
      </span>
      <button
        type="button"
        aria-label={`Adicionar ${label}`}
        onClick={() => onChange(value + 1)}
        className="flex h-10 w-11 items-center justify-center text-fx-cream transition-transform duration-[120ms] ease-fx active:scale-95"
      >
        <Plus size={16} strokeWidth={2} />
      </button>
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex min-h-14 w-full items-center gap-4 text-left"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-semibold text-fx-title">{label}</span>
        {description && <span className="text-[12.5px] text-fx-faint">{description}</span>}
      </span>
      <span
        className={cn(
          "relative h-[30px] w-[50px] shrink-0 rounded-full transition-colors duration-[180ms] ease-fx",
          checked ? "bg-fx-red" : "bg-fx-raised border border-fx-line",
        )}
      >
        <span
          className={cn(
            "absolute top-[3px] size-6 rounded-full transition-[left,background-color] duration-[180ms] ease-fx",
            checked ? "left-[23px] bg-fx-cream" : "left-[3px] bg-fx-faint",
          )}
        />
      </span>
    </button>
  );
}

export function Callout({
  icon,
  children,
  className,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border border-fx-red-border-soft bg-fx-red-surface px-4 py-3.5 text-[13.5px] leading-5 text-fx-body [&_svg]:mt-0.5 [&_svg]:shrink-0 [&_svg]:text-fx-red-icon",
        className,
      )}
    >
      {icon}
      <p>{children}</p>
    </div>
  );
}

export const totalLabel = pad2;
